import * as Yup from 'yup';
import classnames from 'classnames';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { GoogleCaptchaV2 } from '@/components/GoogleCaptchaV2';
import { InputTextField } from '@/components/UI/InputTextField';
import { PasswordField } from '@/components/UI/PasswordField';
import { RoundButton } from '@/components/UI/RoundButton';
import { strongPasswordRegex } from '@/helpers/common';
import { RoundLink } from '@/components/UI/RoundLink';
import Checkbox from '@/components/UI/Checkbox';
import { APP_LINKS } from '@/helpers/links';
import './index.scss';

const INITIAL_VALUES = {
  email: '',
  password: '',
  termsAccept: false,
  privacyAccept: false,
  captchaPassed: false,
};

const VALIDATION_SCHEMA = t =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('register_form.invalid_email'))
      .required(t('register_form.email_required')),
    password: Yup.string()
      .matches(strongPasswordRegex, t('register_form.password_requirements'))
      .required(t('register_form.password_required')),
    termsAccept: Yup.boolean().oneOf([true], t('register_form.must_accept_terms')),
    privacyAccept: Yup.boolean().oneOf([true], t('register_form.must_accept_privacy')),
    captchaPassed: Yup.boolean().oneOf([true], t('register_form.check_robot')),
  });

const RegisterForm = ({ onSubmit, role }) => {
  const { t } = useTranslation('auth');
  return (
    <Formik
      validationSchema={VALIDATION_SCHEMA(t)}
      initialValues={INITIAL_VALUES}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
        <form className="register-form" onSubmit={handleSubmit}>
          <h1 className="register-form__title f-34 f-500">
            {t('register_form.create_account', { role: role.replace('_', '-') })}
          </h1>
          <InputTextField
            label={t('register_form.email_label', { required: '*' })}
            name="email"
            value={values.email}
            onChange={e => setFieldValue('email', e.target.value.trim())}
            onClear={() => setFieldValue('email', '')}
            className="register-form__email"
            error={errors.email && touched.email && errors.email}
          />
          <PasswordField
            value={values.password}
            onChange={handleChange}
            onClear={() => setFieldValue('password', '')}
            error={errors.password && touched.password && errors.password}
            className="register-form__password"
            withValidation
          />

          <div className="register-form__terms">
            <Checkbox
              name="termsAccept"
              isChecked={values.termsAccept}
              onChange={handleChange}
              error={errors.termsAccept && touched.termsAccept}
              render={() => (
                <span
                  className={classnames(
                    'f-12',
                    errors.termsAccept && touched.termsAccept && 'error',
                  )}
                >
                  {t('register_form.confirm_terms')} <br />{' '}
                  <a href={APP_LINKS.docTermsOfUse} target="_blank" rel="noopener noreferrer">
                    {t('register_form.terms_of_use')}
                  </a>
                </span>
              )}
            />
            <Checkbox
              name="privacyAccept"
              isChecked={values.privacyAccept}
              onChange={handleChange}
              error={errors.privacyAccept && touched.privacyAccept}
              render={() => (
                <span
                  className={classnames(
                    'f-12',
                    errors.privacyAccept && touched.privacyAccept && 'error',
                  )}
                >
                  {t('register_form.confirm_privacy')} <br />{' '}
                  <a href={APP_LINKS.docPrivacyAndPolicy} target="_blank" rel="noopener noreferrer">
                    {t('register_form.privacy_policy')}
                  </a>
                </span>
              )}
            />
          </div>

          <GoogleCaptchaV2
            className="register-form__captcha"
            onChange={token => setFieldValue('captchaPassed', !!token)}
          />

          <RoundButton
            label={t('register_form.register')}
            className="register-form__submit"
            type="submit"
            disabled={isSubmitting || !!Object.keys(errors).length}
            onSubmit={handleSubmit}
            fillBackground
            fullWidth
            withArrow
          />
          <div className="register-form__bottom">
            <span>{t('register_form.already_registered')}</span>
            <RoundLink label={t('register_form.sign_in')} path="/sign-in" />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default RegisterForm;
